<template>
    <el-form-item :class="customClass" :label="label" :prop="prop" label-position="top" class="w-100">
        <el-select
            v-model="selectedValue"
            :multiple="multiple"
            :clearable="clearable"
            :disabled="disabled || loading"
            filterable
            :placeholder="placeholder || 'Select'"
            size="large"
            default-first-option
            :loading="loading"
            @visible-change="handleDropdownToggle"
            popper-append-to-body
            placement="bottom-start"
        >
            <template #prefix>
                <i v-if="!loading" :class="`text-primary ti ti-${icon}`"></i>
                <el-icon v-else><Loading /></el-icon>
            </template>

            <el-empty v-if="error" :description="errorMessage" />
            <el-option v-for="item in items" :key="item[valueKey]" :label="item[displayKey]" :value="item[valueKey]" />
        </el-select>
    </el-form-item>
</template>

<script setup lang="ts">
    import { computed, watch, defineExpose, ref, onMounted, onUnmounted } from 'vue'
    import { debounce } from 'lodash-es'
    import { useApi } from '@/composables/useApi'
    import { Loading } from '@element-plus/icons-vue'

    const props = defineProps({
        modelValue: {
            type: [String, Number, Array, null],
            default: null,
            required: true,
        },
        endpoint: { type: String, required: false },
        label: String,
        customClass: String,
        placeholder: String,
        prop: String,
        display: { type: String, default: 'name' },
        icon: { type: String, default: 'list-check' },
        value: { type: String, default: 'id' },
        clearable: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        autoSelectFirst: { type: Boolean, default: false },
        multiple: { type: Boolean, default: false },
        reloadKey: { type: Number, default: 0 },
        dataPath: { type: String, default: 'data' },
        refreshOnOpen: { type: Boolean, default: false },
        refreshInterval: { type: Number, default: 0 }, // in milliseconds
    })

    const emit = defineEmits(['update:modelValue', 'refreshed'])

    const {
        data,
        isFetching: loading,
        refetch,
        error,
    } = useApi({
        endpoint: props.endpoint,
    })

    // Convert prop names to match your data exactly
    const displayKey = computed(() => props.display)
    const valueKey = computed(() => props.value)

    // Selected value ref
    const selectedValue = ref(props.modelValue)

    // Track last refresh time
    const lastRefreshTime = ref(0)
    const dropdownOpenCount = ref(0)

    // Items from API
    const items = computed(() => {
        if (!data.value) return []

        // Handle direct array response
        if (Array.isArray(data.value)) return data.value

        // Handle nested data path
        const result = props.dataPath.split('.').reduce((obj, key) => {
            return obj?.[key] ?? []
        }, data.value)

        return Array.isArray(result) ? result : []
    })

    const errorMessage = computed(() => error?.value?.message || 'Failed to load options')

    // Watch for modelValue changes from parent
    watch(
        () => props.modelValue,
        newVal => {
            selectedValue.value = newVal
        },
    )

    // Emit updates
    watch(selectedValue, newVal => {
        emit('update:modelValue', newVal)
    })

    // Auto-select first item
    watch(
        items,
        newItems => {
            if (props.autoSelectFirst && !selectedValue.value && newItems.length > 0) {
                selectedValue.value = newItems[0][valueKey.value]
            }
        },
        { immediate: true },
    )

    // Handle dropdown toggle
    const handleDropdownToggle = (isOpen: boolean) => {
        if (isOpen && props.refreshOnOpen) {
            // Only refresh if:
            // 1. It's the first time opening
            // 2. Or refreshInterval has passed since last refresh
            const now = Date.now()
            if (
                dropdownOpenCount.value === 0 ||
                (props.refreshInterval > 0 && now - lastRefreshTime.value > props.refreshInterval)
            ) {
                refetchData()
            }
            dropdownOpenCount.value++
        }
    }

    // Enhanced refetch method
    const refetchData = async () => {
        try {
            await refetch()
            lastRefreshTime.value = Date.now()
            emit('refreshed', items.value)
        } catch (err) {
            console.error('Error refreshing data:', err)
        }
    }

    // Refetch when endpoint or reloadKey changes
    watch(
        [() => props.endpoint, () => props.reloadKey],
        debounce(() => {
            refetchData()
        }, 300),
    )

    // Set up interval refresh if needed
    let refreshIntervalId: number | null = null

    onMounted(() => {
        if (props.endpoint) {
            refetchData()
        }

        if (props.refreshInterval > 0) {
            refreshIntervalId = window.setInterval(() => {
                refetchData()
            }, props.refreshInterval)
        }
    })

    onUnmounted(() => {
        if (refreshIntervalId) {
            clearInterval(refreshIntervalId)
        }
    })

    defineExpose({
        reload: refetchData,
        items: computed(() => items.value),
        lastRefreshTime: computed(() => lastRefreshTime.value),
    })
</script>
<style scoped>
    .fixed-dropdown-width::v-deep(.el-select-dropdown) {
        width: 100% !important; /* match the input width */
        min-width: 0 !important; /* remove minimum width that shifts dropdown */
    }
</style>
